import { useEffect, useRef, useState } from "react";
import { Camera, FileText, Upload, Trash2, ExternalLink, Download, Printer, Loader2, FileDown } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { Btn } from "./common";

export const CERTIFICATION = "We hereby certify that the amounts declared in this collection report have been counted, examined, and recorded accurately and faithfully to the best of our knowledge, and that the information submitted reflects the actual collection received on the date indicated.";
const MUST = ["Name of the GKK and Unit, if applicable", "Date and location of the collection", "Type of religious service, activity or special collection",
  "Individual collection amounts and totals", "Exact total amount collected", "Amount remitted to the parish / retained by the GKK, if applicable",
  "Name and signature of the Treasurer or authorized person in charge", "Name and signature of the GKK President or GKK Auditor", "The certification statement"];

const blobOf = async (id) => (await api.get(`/attachments/${id}/file`, { responseType: "blob" })).data;

function useBlobUrl(id, enabled) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!enabled) return undefined;
    let u = null;
    blobOf(id).then((b) => { u = URL.createObjectURL(b); setUrl(u); }).catch(() => setUrl(""));
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [id, enabled]);
  return url;
}

const isImg = (a) => a.content_type?.startsWith("image/");

async function openDoc(a, mode) {
  const w = mode === "download" ? null : window.open("", "_blank");
  try {
    const b = await (mode === "download" ? api.get(`/attachments/${a.id}/file`, { params: { download: true }, responseType: "blob" }).then((r) => r.data) : blobOf(a.id));
    const url = URL.createObjectURL(b);
    if (mode === "download") {
      const el = document.createElement("a");
      el.href = url; el.download = a.filename; el.click();
    } else if (w) {
      w.location.href = url;
      if (mode === "print") setTimeout(() => { try { w.print(); } catch (err) { /* user can print from viewer */ } }, 1200);
    } else {
      window.location.href = url;
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) { if (w) w.close(); toast.error(errMsg(e)); }
}

function DocCard({ a, i, onRemove }) {
  const url = useBlobUrl(a.id, isImg(a));
  return (
    <div data-testid={`doc-item-${i}`} className="rounded-xl border border-[#E4DACD] bg-white overflow-hidden flex flex-col">
      <button type="button" onClick={() => openDoc(a, "open")} className="h-40 bg-[#F3EDE2] flex items-center justify-center" data-testid={`doc-preview-${i}`} aria-label="Open document">
        {isImg(a) ? (url ? <img src={url} alt={a.filename} className="h-full w-full object-contain" /> : <Loader2 className="h-6 w-6 animate-spin text-[#6E6259]" />)
          : <div className="text-center text-[#5C4033]"><FileText className="h-10 w-10 mx-auto" /><span className="text-xs font-bold">PDF</span></div>}
      </button>
      <div className="p-2.5 text-xs">
        <div className="font-semibold truncate" title={a.filename}>Page {i + 1} · {a.filename}</div>
        <div className="text-[#6E6259]">{Math.ceil((a.size || 0) / 1024)} KB · {a.uploaded_by_name}</div>
        <div className="flex flex-wrap gap-1 mt-2">
          <Btn sm v="ghost" type="button" onClick={() => openDoc(a, "open")} data-testid={`doc-open-${i}`}><ExternalLink className="h-4 w-4" /></Btn>
          <Btn sm v="ghost" type="button" onClick={() => openDoc(a, "download")} data-testid={`doc-download-${i}`}><Download className="h-4 w-4" /></Btn>
          <Btn sm v="ghost" type="button" onClick={() => openDoc(a, "print")} data-testid={`doc-print-${i}`}><Printer className="h-4 w-4" /></Btn>
          {onRemove && <Btn sm v="danger" type="button" onClick={() => onRemove(a)} data-testid={`doc-remove-${i}`}><Trash2 className="h-4 w-4" /> Replace / remove</Btn>}
        </div>
      </div>
    </div>
  );
}

export function DocGallery({ items, onRemove }) {
  if (!items?.length) return <p className="text-sm text-[#6E6259]" data-testid="doc-empty">No document uploaded yet.</p>;
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="doc-gallery">{items.map((a, i) => <DocCard key={a.id} a={a} i={i} onRemove={onRemove} />)}</div>;
}

export function SheetLink({ gkkId, unitId }) {
  const get = async () => {
    const w = window.open("", "_blank");
    try {
      const r = await api.get("/forms/collection-sheet", { params: { gkk_id: gkkId || "", unit_id: unitId || "" }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      if (w) w.location.href = url; else window.location.href = url;
    } catch (e) { if (w) w.close(); toast.error(errMsg(e)); }
  };
  return <Btn sm v="outline" type="button" onClick={get} data-testid="blank-sheet-button"><FileDown className="h-4 w-4" /> Printable blank collection sheet</Btn>;
}

export function DocumentUpload({ gkkId, unitId, items, onChange, confirmed, onConfirm, offline, receipt = false }) {
  const p = receipt ? "qr" : "doc";
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const [progress, setProgress] = useState(null);
  const upload = async (files) => {
    if (!gkkId) return toast.error("Please choose the GKK first");
    let list = [...items];
    for (const file of Array.from(files || [])) {
      if (!/^(image\/(jpeg|png)|application\/pdf)$/.test(file.type)) { toast.error(`${file.name}: only JPG, PNG or PDF`); continue; }
      if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name} is larger than 10 MB`); continue; }
      const fd = new FormData();
      fd.append("file", file); fd.append("gkk_id", gkkId); fd.append("unit_id", unitId || "");
      try {
        setProgress({ name: file.name, pct: 0 });
        const { data } = await api.post("/attachments", fd, { onUploadProgress: (e) => setProgress({ name: file.name, pct: Math.round((e.loaded / (e.total || 1)) * 100) }) });
        list = [...list, data];
        onChange(list);
      } catch (e) { toast.error(`${file.name}: ${errMsg(e)}`); }
    }
    setProgress(null);
    if (fileRef.current) fileRef.current.value = "";
    if (camRef.current) camRef.current.value = "";
  };
  const remove = async (a) => {
    if (!window.confirm(`Remove "${a.filename}"? You can upload a clearer copy afterwards.`)) return;
    try { await api.delete(`/attachments/${a.id}`); } catch (e) { if (e.response?.status !== 404) return toast.error(errMsg(e)); }
    onChange(items.filter((x) => x.id !== a.id));
    onConfirm(false);
  };
  return (
    <div className="space-y-4" data-testid={receipt ? "qr-receipt-section" : "document-upload-section"}>
      {receipt ? (
        <div className="rounded-xl bg-[#FAF7F2] border border-[#EFE7DB] p-4 text-sm"><p className="font-semibold mb-1">Attach the successful transaction receipt from your bank or e-wallet app (screenshot, photo, scan or PDF). It should clearly show the amount, date and time, reference number, recipient (ST FRANCIS AP MINISTRY) and the word Successful.</p>
          <p className="text-xs text-[#6E6259]">The receipt is supporting evidence only. The Parish Office confirms the money actually arrived in the Ministry Fund account.</p></div>
      ) : (
      <div className="rounded-xl bg-[#FAF7F2] border border-[#EFE7DB] p-4 text-sm">
        <p className="font-semibold mb-1">Upload a clear photo, scan or PDF of the original signed collection record. It must show:</p>
        <ul className="list-disc pl-5 space-y-0.5 text-[#4B4038]">{MUST.map((m) => <li key={m}>{m}</li>)}</ul>
        <p className="mt-2 italic text-[#5C4033]">“{CERTIFICATION}”</p>
        <p className="mt-2 text-xs text-[#6E6259]">Two signatures are required: the Treasurer / person in charge, and the GKK President or GKK Auditor. The document is supporting evidence; the Parish Office still verifies the amounts.</p>
        <div className="mt-3"><SheetLink gkkId={gkkId} unitId={unitId} /></div>
      </div>)}
      {offline ? <p className="rounded-xl bg-[#FEF3C7] p-3 text-sm font-semibold text-[#92400E]" data-testid={`${p}-offline-note`}>Documents can be uploaded once you are back online.</p> : (
        <div className="flex flex-wrap gap-3">
          <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => upload(e.target.files)} data-testid={`${p}-camera-input`} />
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,application/pdf,.jpg,.jpeg,.png,.pdf" multiple className="hidden" onChange={(e) => upload(e.target.files)} data-testid={`${p}-file-input`} />
          <Btn type="button" v="terra" onClick={() => camRef.current?.click()} disabled={!!progress} data-testid={`${p}-camera-button`}><Camera className="h-5 w-5" /> {receipt ? "Take a Photo" : "Take photo"}</Btn>
          {receipt && <Btn type="button" v="outline" onClick={() => camRef.current?.click()} disabled={!!progress} data-testid="qr-scan-button"><Camera className="h-5 w-5" /> Scan Receipt</Btn>}
          <Btn type="button" v="outline" onClick={() => fileRef.current?.click()} disabled={!!progress} data-testid={`${p}-choose-button`}><Upload className="h-5 w-5" /> {receipt ? "Upload Receipt" : "Choose files (JPG, PNG, PDF)"}</Btn>
        </div>
      )}
      {progress && <div className="text-sm" data-testid="doc-upload-progress"><Loader2 className="inline h-4 w-4 animate-spin mr-1" /> Uploading {progress.name}… {progress.pct}%
        <div className="h-2 mt-1 rounded bg-[#E4DACD]"><div className="h-2 rounded bg-[#2D6A4F]" style={{ width: `${progress.pct}%` }} /></div></div>}
      <DocGallery items={items} onRemove={remove} />
      <label className={`flex gap-3 items-start rounded-xl border-2 p-4 cursor-pointer ${items.length ? "border-[#2D6A4F] bg-[#E8F5E9]" : "border-[#E4DACD] bg-white opacity-60"}`}>
        <input type="checkbox" disabled={!items.length} checked={confirmed} onChange={(e) => onConfirm(e.target.checked)} className="mt-1 h-6 w-6 accent-[#2D6A4F]" data-testid={`${p}-confirm-checkbox`} />
        <span>{receipt ? "I confirm the receipt clearly shows the amount, date and time, reference number, recipient and successful status." : "I confirm that all amounts, dates, names and both signatures are clearly visible in the uploaded document(s)."}</span>
      </label>
    </div>
  );
}
